<script setup lang="ts">
import { computed } from 'vue';

import { formatDecimal } from '@/shared/lib';

import type { ChartGranularity, ChartPoint } from './chart-point-window';

import { formatChartAxisLabel, formatChartPointWindow } from './chart-point-window';
import { buildIndexScale, buildLabelIndexes, buildPaddedExtentScale, buildValueTicks } from './chart-scales';
import ChartCanvas from './ChartCanvas.vue';
import ChartTooltip from './ChartTooltip.vue';
import { useChartCanvas } from './use-chart-canvas';

interface Props {
  /** Незавершённая точка (`incomplete`) рисуется пунктирным сегментом и полой точкой. */
  points: ChartPoint[];
  granularity?: ChartGranularity;
  label?: string;
  valueLabel?: string;
  incompleteHint?: string;
  valueFormatter?: (value: number) => string;
}

const props = withDefaults(defineProps<Props>(), {
  granularity: 'Day',
  label: 'Динамика показателя',
  valueLabel: 'Значение',
  incompleteHint: undefined,
  valueFormatter: (value: number) => formatDecimal(value),
});

const { activeIndex, canvas, geometry } = useChartCanvas(
  'line-chart',
  () => props.label,
  () => props.points.length,
);

/**
 * Ось масштабируется по размаху, а не от нуля: у линии значение кодируется
 * положением точки, а не длиной, поэтому ненулевая база не искажает пропорции
 * и делает видимой динамику уровня, которая от нуля выглядит плоской.
 */
const valueScale = computed(() =>
  buildPaddedExtentScale(
    props.points.map((point) => point.value),
    [geometry.value.plotBottom, geometry.value.plotTop],
  ),
);

const valueTicks = computed(() => buildValueTicks(valueScale.value, 4));
const indexScale = computed(() =>
  buildIndexScale(props.points.length, [geometry.value.plotLeft, geometry.value.plotRight]),
);

const coordinates = computed(() =>
  props.points.map((point, index) => ({
    index,
    point,
    x: indexScale.value(index),
    y: valueScale.value(point.value),
    isIncomplete: point.incomplete === true,
  })),
);

/** Сплошной путь обрывается перед незавершённой точкой — её дорисовывает пунктир. */
const solidPath = computed(() => {
  const solid = coordinates.value.filter((item) => !item.isIncomplete);

  return solid.map((item, index) => `${index === 0 ? 'M' : 'L'}${item.x},${item.y}`).join(' ');
});

const incompletePath = computed(() => {
  const incomplete = coordinates.value.find((item) => item.isIncomplete);
  const previous = incomplete ? coordinates.value[incomplete.index - 1] : undefined;

  return incomplete && previous ? `M${previous.x},${previous.y} L${incomplete.x},${incomplete.y}` : '';
});

const activePoint = computed(() =>
  activeIndex.value === null ? null : (coordinates.value[activeIndex.value] ?? null),
);
const visibleLabelIndexes = computed(() => buildLabelIndexes(props.points.length));
const incompleteHint = computed(
  () => props.incompleteHint ?? (props.granularity === 'Day' ? 'день не закончен' : 'период не завершён'),
);
</script>

<template>
  <ChartCanvas
    v-bind="canvas"
    :geometry="geometry"
    :ticks="valueTicks"
    :scale="valueScale"
    :formatter="valueFormatter"
  >
    <line
      v-if="activePoint"
      :x1="activePoint.x"
      :x2="activePoint.x"
      :y1="geometry.plotTop"
      :y2="geometry.plotBottom"
      stroke="var(--base-divider-soft)"
      stroke-width="1"
      vector-effect="non-scaling-stroke"
      aria-hidden="true"
    />

    <path
      :d="solidPath"
      fill="none"
      stroke="var(--base-accent-color)"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      vector-effect="non-scaling-stroke"
    />
    <path
      v-if="incompletePath"
      :d="incompletePath"
      fill="none"
      stroke="var(--base-accent-color)"
      stroke-width="2"
      stroke-dasharray="4 4"
      stroke-linecap="round"
      vector-effect="non-scaling-stroke"
      :opacity="0.6"
    />

    <g
      v-for="item in coordinates"
      :key="item.index"
    >
      <circle
        :cx="item.x"
        :cy="item.y"
        :r="activePoint?.index === item.index ? 5 : 3"
        :fill="item.isIncomplete ? 'var(--base-container-bg)' : 'var(--base-accent-color)'"
        stroke="var(--base-accent-color)"
        stroke-width="2"
        vector-effect="non-scaling-stroke"
      />

      <text
        v-if="visibleLabelIndexes.has(item.index)"
        :x="item.x"
        :y="geometry.xLabelY"
        text-anchor="middle"
        fill="var(--base-popup-secondary-color)"
        font-size="12"
      >
        {{ formatChartAxisLabel(item.point.date, granularity) }}
      </text>
    </g>
    <template #tooltip>
      <ChartTooltip
        v-if="activePoint"
        :anchor-percent="(activePoint.x / geometry.viewWidth) * 100"
      >
        <div class="grid gap-1">
          <p class="text-(--base-popup-secondary-color)">
            {{ formatChartPointWindow(activePoint.point.date, granularity) }}
          </p>
          <p>
            {{ valueLabel }}: {{ valueFormatter(activePoint.point.value) }}
            <span v-if="activePoint.isIncomplete"> · {{ incompleteHint }}</span>
          </p>
        </div>
      </ChartTooltip>
    </template>
  </ChartCanvas>
</template>
